import { memo, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
} from '@microsoft/signalr';
import {
  actionsOnlyToAdminOrCoordinator,
  actionsOnlyToPlotting,
  INotification,
  listActivitiesActions,
  listDisciplinesActions,
  listFoldersAndFilesActions,
  listPlottingActions,
  NotificationActionEnum,
  NotificationTypeEnum,
} from 'Data/interfaces/Notification/INotification';
import {
  NOTIFICATIONS_QUERY_KEY,
  NOTIFICATIONS_TEMPORARY_QUERY_KEY,
  NOTIFICATIONS_COUNT_QUERY_KEY,
  NOTIFICATIONS_IN_APP_OBRA_COUNT_QUERY_KEY,
  NOTIFICATIONS_IN_APP_OBRA_QUERY_KEY,
  NOTIFICATIONS_PAGE_QUERY_KEY,
  NOTIFICATIONS_PERSONAL_KEY,
  PREFERENCES_NOTIFICATIONS_QUERY_KEY,
  NOTIFICATIONS_IN_APP_TOPIC_QUERY_KEY,
  OBRA_QUERY_KEY,
} from 'Data/keys/QueryKeys';
import { INotificationData as INotificationDataOld } from 'Data/interfaces/Notification/INotificationResponse';
import { INotificationData } from 'Data/interfaces/Notification/INotification';
import { ISharedUser } from 'Data/interfaces/User/ISharedUser';
import {
  DisciplineNotificationPreferenceEnum,
  FileNotificationPreferenceEnum,
  IPreferences,
  PlottingNotificationPreferenceEnum,
  TopicNotificationPreferenceEnum,
} from 'Data/interfaces/Preferences/IPreferencesApi';
import { getCurrentTenant, getTenants } from 'Store/Tenant/Tenant.selector';
import { useFeatureFlag } from 'Hooks/useFeatureFlag';
import AccessPermission from 'Utils/AcessPermission';
import { useQueryPreferences } from 'Hooks/preferences/useQueryPreferences';
import { useQueryReportEmails } from 'Hooks/report/useQueryReportEmails';
import { useQueryPreferencesEmail } from 'Hooks/preferences/useQueryPreferencesEmail';
import { queryClient } from 'Utils/queryClient';
import { Mixpanel } from 'Utils/MixPanel';
import { AuthUtils } from 'Utils/AuthUtils';
import { getUserInfo } from 'Store/Auth/Auth.selector';
import { useQueryObra } from 'Hooks/obras/useQueryObra';
import { IObraResponse } from 'Data/interfaces/Obra/IObraListResponse';
import { useQueryFetchObra } from 'Hooks/obras/useQueryFetchObra';

const NotificationSignalR = () => {
  const preferencesFeatureFlag = useFeatureFlag('preferences-feature');
  const preferencesFeature = preferencesFeatureFlag.enabled && preferencesFeatureFlag.value === 'test';

  useQueryReportEmails({ enabled: preferencesFeature });
  useQueryPreferencesEmail({
    enabled: preferencesFeature,
  });
  useQueryPreferences({
    enabled: preferencesFeature,
  });
  useQueryObra();
  const { data: obras } = useQueryFetchObra();

  const userInfo = useSelector(getUserInfo);
  const tenants = useSelector(getTenants);
  const currentTenant = useSelector(getCurrentTenant);
  const [connection, setConnection] = useState<HubConnection>();

  const getNotificationData = useCallback((data: string | undefined) => {
    if (!data) { return null; }
    try {
      return JSON.parse(data) as INotificationData;
    } catch (error) {
      return null;
    }
  }, []);

  const verifyIsGrouped = useCallback((item: INotification) => {
    const groupedPlottingActions = [
      NotificationActionEnum.PlottingStatusChanged,
      NotificationActionEnum.PlottingFileDeleted,
      NotificationActionEnum.PlottingRequestDeleted,
    ];
    const agroupedByPlotting = item.Type === NotificationTypeEnum.Plotting &&
      groupedPlottingActions.includes(item.Action);
    const agroupedByActivity = item.Type === NotificationTypeEnum.Activity &&
      item.Action === NotificationActionEnum.TopicAttachmentAdded;

    return agroupedByPlotting || agroupedByActivity;
  }, []);

  useEffect(() => {
    if (connection) {
      connection.stop();
    }

    if (obras && obras.length > 0 && !connection) {
      const constructionsIds = obras.map((obra) => obra.ConstructionSiteId);
      const newConnection = new HubConnectionBuilder()
        .withUrl(
          `${process.env.REACT_APP_BASE_API_URL?.toString().replace('/api', '')}/hubs/notifications?csIds=[${constructionsIds}]`,
        )
        .configureLogging(LogLevel.Information)
        .withAutomaticReconnect()
        .build();

      newConnection.serverTimeoutInMilliseconds = 100000;
      setConnection(newConnection);
    }
  }, [connection, obras]);

  useEffect(() => {
    if (connection) {
      connection
        .start()
        .then(() => {
          connection.on('newNotification', async (message) => {
            let data: INotification | INotificationDataOld = JSON.parse(message);
            const userId = userInfo?.Id || AuthUtils.getLoggedUser()?.userId;

            if (data && userId) {
              data = data as INotification;

              let isAllowed = false;
              if (preferencesFeature) {
                const preferencesNotificationsData = queryClient.getQueryData<IPreferences[]>(
                  [PREFERENCES_NOTIFICATIONS_QUERY_KEY],
                );
                const findPreference = preferencesNotificationsData?.find((preference) => {
                  return preference.Action === data.Action;
                });
                if (findPreference) {
                  const preferenceValue = findPreference.Preference as number;
                  if (listFoldersAndFilesActions.includes(findPreference.Action)) {
                    isAllowed = preferenceValue !== FileNotificationPreferenceEnum.None;
                  }
                  if (listActivitiesActions.includes(findPreference.Action)) {
                    isAllowed = preferenceValue === TopicNotificationPreferenceEnum.All;
                    if (
                      !isAllowed &&
                      preferenceValue === TopicNotificationPreferenceEnum.Followed &&
                      data.TargetUsers &&
                      data.TargetUsers.length > 0
                    ) {
                      isAllowed = data.TargetUsers.some((user) => user.UserFk === userId);
                    }
                  }
                  if (listDisciplinesActions.includes(findPreference.Action)) {
                    isAllowed = preferenceValue !== DisciplineNotificationPreferenceEnum.None;
                  }
                  if (listPlottingActions.includes(findPreference.Action)) {
                    isAllowed = preferenceValue !== PlottingNotificationPreferenceEnum.None;
                  }
                }
              } else {
                isAllowed = true;
              }

              let hasPlottingPermission = false;
              let hasControledPermission = false;
              const userIsTarget = data?.TargetUsers?.find((user) => {
                return user?.UserFk === userId;
              });
              const isControledTypeAction = actionsOnlyToAdminOrCoordinator.includes(
                data.Action,
              );
              if (AccessPermission.isAdmin()) {
                hasPlottingPermission = true;
                hasControledPermission = true;
              }
              if (
                (!hasPlottingPermission || !hasControledPermission) &&
                actionsOnlyToPlotting.includes(data.Action)
              ) {
                const obrasList = queryClient.getQueryData<IObraResponse[]>(
                  [OBRA_QUERY_KEY],
                );
                const findObra = obrasList?.find((obra) => {
                  return obra.ConstructionSiteId === Number(data.ConstructionSiteFk);
                });
                if (findObra) {
                  const userPemissions = findObra.SharedUsers?.find((user: ISharedUser) => {
                    return user.UserFk === userId || user.User?.Id === userId;
                  }) as ISharedUser | undefined;

                  if (userPemissions) {
                    hasControledPermission = true;
                    hasPlottingPermission = userPemissions.HasPlottingPermission;
                  }
                }
              } else {
                hasPlottingPermission = true;
              }

              if (
                hasPlottingPermission &&
                (!data?.IsTargeted || (data?.IsTargeted && userIsTarget)) &&
                (!isControledTypeAction || (isControledTypeAction && hasControledPermission))
              ) {
                await queryClient.invalidateQueries({
                  queryKey: [NOTIFICATIONS_PAGE_QUERY_KEY],
                });

                if (
                  data?.CreatedByUser?.Id &&
                  data.CreatedByUser.Id !== userId
                ) {
                  await Promise.all([
                    queryClient.invalidateQueries({
                      queryKey: [NOTIFICATIONS_IN_APP_OBRA_COUNT_QUERY_KEY],
                      exact: true,
                    }),
                    queryClient.invalidateQueries({
                      queryKey: [NOTIFICATIONS_IN_APP_OBRA_QUERY_KEY, Number(data.ConstructionSiteFk)],
                      exact: true,
                    }),
                    queryClient.invalidateQueries({
                      queryKey: [NOTIFICATIONS_IN_APP_TOPIC_QUERY_KEY, Number(data.ConstructionSiteFk)],
                      exact: true,
                    }),
                  ]);

                  if (isAllowed) {
                    await Promise.all([
                      queryClient.cancelQueries({
                        queryKey: [NOTIFICATIONS_COUNT_QUERY_KEY],
                        exact: true,
                      }),
                      queryClient.cancelQueries({
                        queryKey: [NOTIFICATIONS_COUNT_QUERY_KEY, Number(data.ConstructionSiteFk)],
                        exact: true,
                      }),
                    ]);

                    const isGrouped = verifyIsGrouped(data);
                    if (isGrouped) {
                      await Promise.all([
                        queryClient.setQueryData(
                          [NOTIFICATIONS_TEMPORARY_QUERY_KEY],
                          [],
                        ),
                        queryClient.setQueryData(
                          [NOTIFICATIONS_TEMPORARY_QUERY_KEY, Number(data.ConstructionSiteFk)],
                          [],
                        ),
                        queryClient.invalidateQueries({
                          queryKey: [NOTIFICATIONS_QUERY_KEY],
                          exact: true,
                        }),
                        queryClient.invalidateQueries({
                          queryKey: [NOTIFICATIONS_QUERY_KEY, Number(data.ConstructionSiteFk)],
                          exact: true,
                        }),
                      ]);
                    } else {
                      await Promise.all([
                        queryClient.setQueryData(
                          [NOTIFICATIONS_TEMPORARY_QUERY_KEY],
                          (oldData: INotification[]) => {
                            if (oldData && oldData.length > 0) { return [...oldData, data]; }
                            return [data];
                          },
                        ),
                        queryClient.setQueryData(
                          [NOTIFICATIONS_TEMPORARY_QUERY_KEY, Number(data.ConstructionSiteFk)],
                          (oldData: INotification[]) => {
                            if (oldData && oldData.length > 0) { return [...oldData, data]; }
                            return [data];
                          },
                        ),
                      ])
                    }
                    await Promise.all([
                      queryClient.setQueryData(
                        [NOTIFICATIONS_COUNT_QUERY_KEY],
                        (oldData: number) => {
                          if (oldData > 0) { return oldData + 1; }
                          return 1;
                        },
                      ),
                      queryClient.setQueryData(
                        [NOTIFICATIONS_COUNT_QUERY_KEY, Number(data.ConstructionSiteFk)],
                        (oldData: number) => {
                          if (oldData > 0) { return oldData + 1; }
                          return 1;
                        },
                      ),
                    ])

                    if (
                      data.Type === NotificationTypeEnum.Activity &&
                      data.TargetUsers && data.TargetUsers.length > 0 &&
                      userIsTarget?.IsPersonal
                    ) {
                      await queryClient.cancelQueries({
                        queryKey: [NOTIFICATIONS_COUNT_QUERY_KEY, NOTIFICATIONS_PERSONAL_KEY],
                        exact: true,
                      })

                      if (isGrouped) {
                        await Promise.all([
                          queryClient.setQueryData(
                            [NOTIFICATIONS_TEMPORARY_QUERY_KEY, NOTIFICATIONS_PERSONAL_KEY],
                            [],
                          ),
                          queryClient.invalidateQueries({
                            queryKey: [NOTIFICATIONS_QUERY_KEY, NOTIFICATIONS_PERSONAL_KEY],
                            exact: true,
                          }),
                        ]);
                      } else {
                        await queryClient.setQueryData(
                          [NOTIFICATIONS_TEMPORARY_QUERY_KEY, NOTIFICATIONS_PERSONAL_KEY],
                          (oldData: INotification[]) => {
                            if (oldData && oldData.length > 0) { return [...oldData, data]; }
                            return [data];
                          },
                        );
                      }
                      await queryClient.setQueryData(
                        [NOTIFICATIONS_COUNT_QUERY_KEY, NOTIFICATIONS_PERSONAL_KEY],
                        (oldData: number) => {
                          if (oldData > 0) { return oldData + 1; }
                          return 1;
                        },
                      );
                    }

                    Mixpanel.track({
                      name: 'NEW_NOTIFICATION',
                      props: {
                        constructionSiteId: data.ConstructionSiteFk,
                        notificationId: data.NotificationId,
                        actionId: data.Action,
                        actionName: NotificationActionEnum[data.Action],
                        actionIdDescription: NotificationTypeEnum[data.Type],
                      },
                      userInfo,
                      currentListTenant: currentTenant,
                      tenantIds: tenants && tenants.length > 0
                        ? (tenants?.map((tenant) => tenant?.TenantId)
                          .filter((tenantId) => tenantId !== undefined) as number[])
                        : undefined,
                    });
                  }
                }
              }
            }
          });
        })
        .catch((e) =>
          console.log('Connection failed: ', e)
        );
    }
  }, [
    connection,
    userInfo,
    currentTenant,
    verifyIsGrouped,
    tenants,
    preferencesFeature,
    getNotificationData,
  ]);

  return <></>;
};

export default memo(NotificationSignalR);
